// @flow
import React, {useCallback, useEffect, useMemo, useState, useRef} from 'react'
import {fetchQuery} from 'react-relay'
import environment from 'environment'
import {debounce} from 'lodash'
import AsyncSelect from 'react-select/async'
import type {ConcreteRequest} from 'relay-runtime'
import type {Option} from '../types'
import useOptionFormatting from '../useOptionFormatting'
import useCustomFilterOption from '../useCustomFilterOption'

type Props = {
  name: string,
  orgDatabaseId: string,
  type: string,
  query: ConcreteRequest, // GraphQL query to fetch options
  selectedOption?: Option | null,
  formatOptionData: (node: any) => Option, // Function to setup option data
  placeholder?: string,
  isClearable?: boolean,
  itemsPerPage?: number,
  onSelectionChange?: Function,
};

function ElasticSearchRailsSelect({
  name,
  orgDatabaseId,
  type,
  query,
  selectedOption,
  formatOptionData,
  placeholder = I18n.t('shared.select_dot_dot_dot'),
  isClearable = true,
  itemsPerPage = 50,
  onSelectionChange,
}: Props): React$Node {
  const [options, setOptions] = useState([])
  const [searchVariables, setSearchVariables] = useState({
    orgDatabaseId,
    type,
    keywords: '',
    fetchAllWhenEmpty: true,
    count: itemsPerPage,
  })
  const [data, setData] = useState(null)
  const [selectedValue, setSelectedValue] = useState(null)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [wasCleared, setWasCleared] = useState(false)
  const wrapperRef = useRef(null)

  const {filterOption} = useCustomFilterOption()

  const {formatOptionLabel}
    = useOptionFormatting(options, {})

  const fetchOptions = useCallback(async (variables, append = false) => {
    try {
      const response = await fetchQuery(environment, query, variables).toPromise()
      setData(response)

      if (response?.organization?.esSearchResults?.edges) {
        const mappedResults = response.organization.esSearchResults.edges.map(({node}) => formatOptionData(node))

        if (append)
          setOptions((prevOptions) => [...prevOptions, ...mappedResults])
        else
          setOptions(mappedResults)

        return mappedResults
      }
      return []
    } catch (error) {
      console.error('Error fetching data:', error) // eslint-disable-line no-console
      return []
    }
  }, [query, formatOptionData])

  const fetchData = useCallback(async () => {
    const nodes = await fetchOptions(searchVariables)
    if (nodes)
      setOptions(nodes)
  }, [fetchOptions, searchVariables])

  useEffect(() => {
    fetchData()
  }, [searchVariables, fetchData])

  const debouncedHandleSearch = useMemo(
    () => debounce(async (inputValue, callback) => {
      const updatedVariables = {
        ...searchVariables,
        keywords: inputValue || '',
        fetchAllWhenEmpty: !inputValue,
      }
      setSearchVariables(updatedVariables)

      const nodes = await fetchOptions(updatedVariables)
      callback(nodes)
    }, 300),
    [fetchOptions, searchVariables],
  )

  const handleSearch = useCallback((inputValue) => {
    if (!inputValue) {
      const updatedVariables = {
        ...searchVariables,
        keywords: '',
        fetchAllWhenEmpty: true,
      }
      return fetchOptions(updatedVariables)
    }

    if (inputValue.length < 2)
      return Promise.resolve(options)

    return new Promise((resolve) => {
      debouncedHandleSearch(inputValue, resolve)
    })
  }, [searchVariables, fetchOptions, options, debouncedHandleSearch])

  const handleLoadMore = async () => {
    const pageInfo = data?.organization?.esSearchResults?.pageInfo

    if (isLoadingMore || !pageInfo?.hasNextPage)
      return

    setIsLoadingMore(true)

    const updatedVariables = {
      ...searchVariables,
      count: itemsPerPage,
      after: pageInfo.endCursor,
    }

    await fetchOptions(updatedVariables, true)
    setIsLoadingMore(false)
  }

  const getDefaultValue = useMemo(() => {
    if (!selectedOption?.value)
      return null

    const existingOption = options.find(
      (option) => String(option.value) === String(selectedOption.value),
    )

    return existingOption || selectedOption
  }, [selectedOption, options])

  useEffect(() => {
    if (!wasCleared)
      setSelectedValue(getDefaultValue)
  }, [getDefaultValue, wasCleared])

  const handleChange = useCallback((newValue) => {
    setSelectedValue(newValue === null ? undefined : newValue)
    setWasCleared(newValue === null)
    if (onSelectionChange)
      onSelectionChange(newValue)
    if (!newValue)
      handleSearch('')
  }, [onSelectionChange, handleSearch])

  return (
    <div ref={wrapperRef} className="elastic-search-rails-select-wrapper" test-id={`ElasticSearchRailsSelect-${name}`}>
      <AsyncSelect
        id={name}
        name={name}
        isSearchable
        isClearable={isClearable}
        classNamePrefix="rails_select"
        formatOptionLabel={formatOptionLabel}
        placeholder={placeholder}
        value={selectedValue}
        filterOption={filterOption}
        onChange={handleChange}
        loadOptions={handleSearch}
        defaultOptions={options}
        menuPlacement="auto"
        onMenuScrollToBottom={handleLoadMore}
        isLoading={isLoadingMore}
        cacheOptions={false}
      />
    </div>
  )
}

export default ElasticSearchRailsSelect
